import React, { useState } from 'react'
import MinimalisticTable, { ColumnDef }
  from '../../../../shared/components/ui/MinimalisticTable'
import ConfirmDeleteButton from '@/shared/components/ui/ConfirmDeleteButton'
import { useTenants } from '../hooks/useTenants'
import type { Tenant } from '../api/tenantsApi'
import CreateTenantDialog from './CreateTenantDialog'
import RelativeTime from '@/shared/components/ui/RelativeTime'

export default function TenantsTable() {
  const [openCreate, setOpenCreate] = useState(false)
  const { data: tenants = [], isLoading, isError, error, remove } = useTenants()

  const columns: ColumnDef<Tenant>[] = [
    { header: 'Code', accessor: 'code' },
    {
      header: 'Display Name',
      accessor: 'display_name',
      Cell: (val: string | null) => (
        <span className="block max-w-[260px] truncate" title={val ?? ''}>
          {val ?? '—'}
        </span>
      ),
    },
    {
      header: 'Description',
      accessor: 'description',
      Cell: (val: string | null) => (
        <span className="block max-w-[320px] truncate" title={val ?? ''}>
          {val ?? '—'}
        </span>
      ),
    },
    {
      header: 'Active',
      accessor: 'active',
      Cell: (val: boolean | null) => (
        <span className={val ? 'text-emerald-400' : 'text-neutral-500'}>
          {val == null ? '—' : val ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      header: 'Created',
      accessor: 'created_at',
      Cell: (val: string | null) => <RelativeTime value={val} />,
    },
    {
      header: '',
      accessor: 'id_tenant' as any,
      Cell: (_val: any, row: Tenant) => (
        <ConfirmDeleteButton
          title="tenant"
          confirmText="DELETE"
          triggerContent={<span>🗑</span>}
          triggerTitle={`Delete tenant "${row.display_name ?? row.code}"`}
          triggerClassName="text-neutral-600 hover:text-rose-400 transition-colors px-1.5 py-1 rounded hover:border-neutral-600"
          isBusy={remove.isPending}
          errorMessage={remove.isError ? (remove.error?.message ?? 'Delete failed.') : null}
          description={
            <>
              <p>
                Permanently delete tenant{' '}
                <span className="font-semibold text-white">
                  {row.display_name ?? row.code}
                </span>{' '}
                ?
              </p>
              <p className="text-xs text-rose-400">
                This drops the tenant row and its outcomes partition. This cannot be undone.
              </p>
            </>
          }
          onConfirm={async () => { await remove.mutateAsync(row.id_tenant) }}
        />
      ),
    },
  ]

  if (isLoading) return <p className="text-neutral-400">Loading…</p>
  if (isError)   return <p className="text-rose-400">Error: {error?.message}</p>

  return (
    <>
      <MinimalisticTable
        columns={columns}
        data={tenants}
        noDataMessage="No tenants."
        showSearch
        searchPlaceholder="Search tenants…"
        showNewButton
        onNew={() => setOpenCreate(true)}
      />

      <CreateTenantDialog open={openCreate} onOpenChange={setOpenCreate} />
    </>
  )
}
